<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
// Vue
import { useI18n } from "vue-i18n"
import { computed, onMounted, ref } from "vue"
// Store
import { useAuthStore } from "@core/stores/auth.store"
// Services
// ⚠ Service `core` autonome (PAS `@modules/courtage`) : importer le service
// courtage depuis un composant `core/` faisait crasher la card à l'init en prod
// (chunk lazy courtage non résolu — DEV-594, rollback front v346→v347).
import { EnergyContractService } from "@core/services/energy-contract.service"
// Types
import type {
    EnergyContract,
    GazStructureTarifaire,
    PeriodeContrat,
} from "@core/types/energy-contract"
import type { LineBlockLine } from "@types"

const authStore = useAuthStore()
const service = new EnergyContractService()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            supplier: "Fournisseur",
            classe: "Classe (ATRD)",
            option_tarifaire: "Option tarifaire",
            prix_kwh: "Prix du kWh (HT)",
            prix_abonnement: "Abonnement (HT)",
            unit: {
                prix_kwh: "centimes/kWh",
                prix_abonnement: "€/an",
            },
            error: "Impossible de charger la tarification.",
            button: {
                modify: "Modifier",
                add_offer: "Ajouter la tarification et le fournisseur",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

type Props = {
    meterId: string
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

// ================================
// REACTIVE STATE
// ================================
// ref pour la modal d'édition des tarifs
const modalEditOffer = ref<boolean>(false)
// contrats gaz de la company couvrant ce compteur
const contrats = ref<EnergyContract[]>([])
const loading = ref<boolean>(false)
const errorMessage = ref<string>("")

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * companyId résolu depuis le store auth (même pattern que
 * DialogEnergyContractGazEdit).
 */
const resolvedCompanyId = computed(
    () => authStore.companyId ?? authStore.companyIdcompany ?? ""
)

/**
 * Contrat gaz applicable à ce compteur (le premier dont une période est
 * applicable, sinon le premier rattaché).
 */
const contratApplicable = computed<EnergyContract | null>(() => {
    if (!contrats.value.length) return null
    // priorité au contrat qui possède une période applicable
    const withPeriode = contrats.value.find((c) =>
        Boolean(getPeriodeApplicable(c))
    )
    return withPeriode ?? contrats.value[0]
})

/**
 * Période applicable du contrat applicable.
 */
const periodeApplicable = computed<PeriodeContrat | null>(() =>
    contratApplicable.value
        ? getPeriodeApplicable(contratApplicable.value)
        : null
)

/**
 * Structure tarifaire gaz de la période applicable.
 */
const structure = computed<GazStructureTarifaire | null>(() => {
    const raw = periodeApplicable.value?.structure_tarifaire
    return raw ? (raw as unknown as GazStructureTarifaire) : null
})

/**
 * Lignes formatées à afficher (dérivées de la structure tarifaire gaz).
 */
const getOfferPCE = computed<LineBlockLine[] | null>(() => {
    const contract = contratApplicable.value
    const struct = structure.value
    if (!contract || !struct) return null

    const lines: LineBlockLine[] = [
        {
            text: t("supplier"),
            value: contract.fournisseur?.raison_sociale || "-",
        },
        {
            text: t("classe"),
            value: struct.classe || "-",
        },
    ]

    // Option tarifaire : uniquement pour les classes T3/T4.
    if (struct.classe === "T3" || struct.classe === "T4") {
        lines.push({
            text: t("option_tarifaire"),
            value: struct.option_tarifaire || "-",
        })
    }

    lines.push(
        {
            text: t("prix_kwh"),
            value: formatValue(
                transformPriceToCentimes(struct.prix_kwh),
                t("unit.prix_kwh")
            ),
        },
        {
            text: t("prix_abonnement"),
            value: formatValue(
                formatNumber(struct.prix_abonnement),
                t("unit.prix_abonnement")
            ),
        }
    )

    return lines
})

// ================================
// METHODS
// ================================

/**
 * Sélectionne la période applicable d'un contrat :
 *  - période sans `date_fin` (ouverte), ou dont [date_debut, date_fin] couvre
 *    aujourd'hui ;
 *  - à défaut, la plus récente (par `date_debut`).
 */
function getPeriodeApplicable(contract: EnergyContract): PeriodeContrat | null {
    const periodes = contract.periodes_validite ?? []
    if (!periodes.length) return null

    const now = Date.now()
    const current = periodes.find((p) => {
        const debut = p.date_debut ? new Date(p.date_debut).getTime() : null
        const fin = p.date_fin ? new Date(p.date_fin).getTime() : null
        if (debut !== null && debut > now) return false
        if (fin !== null && fin < now) return false
        return true
    })
    if (current) return current

    // fallback : la plus récente par date_debut
    return [...periodes].sort(
        (a, b) =>
            new Date(b.date_debut).getTime() - new Date(a.date_debut).getTime()
    )[0]
}

/**
 * format les valeurs
 * @param value valeur (déjà stringifiée) ou null
 * @param unit unité
 */
function formatValue(value: string | null, unit?: string): string {
    if (value === null || value === "") return "-"
    else if (unit) return `${value} ${unit}`
    else return value
}

/**
 * Formate un nombre en chaîne (ou null si absent / NaN).
 */
function formatNumber(value: number | null | undefined): string | null {
    if (value === null || value === undefined || isNaN(value)) return null
    return String(value)
}

/**
 * Convertit un prix €/kWh en centimes/kWh (×100, 2 décimales).
 */
function transformPriceToCentimes(
    price: number | null | undefined
): string | null {
    if (price === null || price === undefined || isNaN(price)) return null
    return Number.parseFloat((price * 100).toFixed(2)).toString()
}

/**
 * Charge les contrats gaz de la company et filtre ceux couvrant ce compteur.
 */
async function loadContracts(): Promise<void> {
    const companyId = resolvedCompanyId.value
    if (!companyId) {
        contrats.value = []
        return
    }
    loading.value = true
    errorMessage.value = ""
    try {
        const all = await service.listEnergyContracts(companyId, "gaz")
        contrats.value = (all ?? []).filter((c) =>
            c._id_sources?.includes(props.meterId)
        )
    } catch {
        errorMessage.value = t("error")
        contrats.value = []
    } finally {
        loading.value = false
    }
}

/**
 * Après save : ferme la modale et recharge les contrats.
 */
async function onSaved(): Promise<void> {
    modalEditOffer.value = false
    await loadContracts()
}

// ================================
// LIFECYCLE
// ================================
onMounted(() => {
    loadContracts()
})
</script>

<template>
    <div class="meter_card_offer_pce">
        <!-- erreur de chargement -->
        <BaseMessage v-if="errorMessage" severity="error" class="mb-2">
            {{ errorMessage }}
        </BaseMessage>

        <!-- si on à des infos on les affiche -->
        <div v-if="getOfferPCE">
            <div v-for="(line, index) in getOfferPCE" :key="index">
                <BaseLineBlock :line="line" />
            </div>
            <!-- on permet de mettre à jour l'offre -->
            <div class="text-center mt-4">
                <BaseButton
                    @click="modalEditOffer = true"
                    :label="t('button.modify')"
                    variant="outlined"
                    severity="contrast"
                    icon="pi pi-pencil"
                ></BaseButton>
            </div>
        </div>
        <!-- sinon on permet d'ajouter les infos -->
        <div v-else-if="!loading" class="text-center">
            <BaseButton
                class="max-w-fit mx-auto mt-4"
                :label="t('button.add_offer')"
                variant="outlined"
                severity="contrast"
                icon="pi pi-pencil"
                @click="modalEditOffer = true"
            ></BaseButton>
        </div>

        <!-- Modal d'édition du contrat gaz (EnergyContract) -->
        <DialogEnergyContractGazEdit
            v-model:visible="modalEditOffer"
            :id-sources="[meterId]"
            :contract="contratApplicable ?? null"
            @saved="onSaved"
        ></DialogEnergyContractGazEdit>
    </div>
</template>

<style scoped></style>
